import { useState, useEffect, useMemo } from 'react';
import { numberInputFormatter } from '../../../utils/utils';
import CloseIcon from '../../../assets/icons/close.svg?react';
import './AiLearningRequestModal.css';
import { positiveNumberInputFormatter } from '../../../utils/utils';
import Selector from '../../../components/Selector/Selector';
import DateTimeInput from '../../../components/DateTimeInput/DateTimeInput';
import { useCreateAiLearning } from '../../../hooks/queries/aiQueries';
import dayjs from 'dayjs';
import AiModuleSelect from './AiModuleSelect';

const AiLearningRequestModal = ({ isOpen, onClose, onSuccess }) => {
  const [predictionModelType, setPredictionModelType] = useState(null);

  // Selector 선택 결과를 관리하는 별도 state
  const [selectedNewModel, setSelectedNewModel] = useState(null);
  const [selectedChargingModule, setSelectedChargingModule] = useState(null);
  // API 호출 훅
  const createAiLearning = useCreateAiLearning();
  
  if (!isOpen) return null;


  const [formDataForNew, setFormDataForNew] = useState({
    "selectedModelName": null,
    "aiModelType": null,
    "selectedModelVersion": null,
    "interationCount": null,
    "startDate": null,
    "endDate": null,
    "isNewModel": true,
    "originAiAlgorithm": null,
    "demandPredictionAiModelName": null,
    "demandPredictionAiModelVersion": null
  });

  const handleFormDataForNewChange = (key, value) => {
    setFormDataForNew({ ...formDataForNew, [key]: value });
  };

  useEffect(() => {
    setFormDataForNew((prev) => ({
      ...prev,
      demandPredictionAiModelName: selectedChargingModule?.ModuleName || null,
      demandPredictionAiModelVersion: selectedChargingModule?.Version || null,
    }));
  },[selectedChargingModule]);

  const modelNameOptions = [
    { id: 'XGBOOST', label: 'XGBOOST' },
    { id: 'DNN', label: 'DNN' },
    { id: 'LSTM', label: 'LSTM' },
  ]
  const predictionModelTypeOptions = [
    { id: 'charging', label: '충전량 예측 모델' },
    { id: 'cbl', label: 'CBL 예측 모델' },
  ];

  // Step 3 완료 버튼 활성화 조건: 모든 필수 입력값이 채워져야 함
  const canComplete = useMemo(() => {
    return (
      !!predictionModelType &&
      formDataForNew.selectedModelName &&
      formDataForNew.interationCount &&
      String(formDataForNew.interationCount).trim() !== '' &&
      parseInt(formDataForNew.interationCount) > 0 &&
      formDataForNew.startDate &&
      formDataForNew.endDate
    );
  }, [formDataForNew, predictionModelType]);

  // API payload 생성 함수
  const buildApiPayload = () => {
    return {
      selectedModelName: formDataForNew.selectedModelName || '',
      aiModelType: predictionModelType === 'charging' ? 'ChargingDemandPrediction' : predictionModelType === 'cbl' ? 'CblPrediction' : '',
      selectedModelVersion: '',
      interationCount: parseInt(formDataForNew.interationCount) || 0,
      startDate: formDataForNew.startDate ? dayjs(formDataForNew.startDate).toISOString() : '',
      endDate: formDataForNew.endDate ? dayjs(formDataForNew.endDate).toISOString() : '',
      isNewModel: true,
      originAiAlgorithm: '',
      demandPredictionAiModelName: formDataForNew.demandPredictionAiModelName || '',
      demandPredictionAiModelVersion: formDataForNew.demandPredictionAiModelVersion || '',
    };
  };

  // 완료 버튼 클릭 핸들러
  const handleComplete = async () => {
    if (!canComplete) return;

    const payload = buildApiPayload();
    if (!payload) {
      alert('잘못된 요청 타입입니다.');
      return;
    }

    try {
      await createAiLearning.mutateAsync(payload);
      if (onSuccess) {
        onSuccess();
      }
      handleClose();
    } catch (error) {
      console.error('AI 학습 요청 실패:', error);
      alert(`AI 학습 요청 등록에 실패했습니다:`);
    }
  };

  const handleClose = () => {
    setPredictionModelType(null);
    setSelectedNewModel(null);
    setFormDataForNew({
      "selectedModelName": null,
      "aiModelType": null,
      "selectedModelVersion": null,
      "interationCount": 0,
      "startDate": null,
      "endDate": null,
      "isNewModel": true,
      "originAiAlgorithm": null,
      "demandPredictionAiModelName": null,
      "demandPredictionAiModelVersion": null
    });
    createAiLearning.reset();
    onClose();
  };

  return (
    <div className="modal-background">
      <div className="modal-container overflow-visible ai-learning-request-modal">
        <div className="modal-header">
          <h3>신규 AI 학습 요청</h3>
          <button className="close-btn" onClick={handleClose}>
            <CloseIcon />
          </button>
        </div>
        
        <div className="modal-content flex flex-col gap-10">
          <div className="ai-learning-step-content mt-14">
            <div className="flex flex-col gap-24 px-16 pb-20 w-full">
              <div className="grid grid-cols-2 gap-10">
                <div className="flex flex-col gap-2">
                  <p className="text-b2-400 text-gray-800">예측 모델 타입</p>
                  <Selector
                    mode="local"
                    options={predictionModelTypeOptions}
                    value={predictionModelTypeOptions.find(
                      (option) => option.id === predictionModelType
                    ) || null}
                    onChange={(value) => {
                      setPredictionModelType(value?.id || null);
                    }}
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <p className="text-b2-400 text-gray-800">모델 선택</p>
                  <Selector
                    mode="local"
                    options={[...modelNameOptions]}
                    value={selectedNewModel}
                    onChange={(value) => {
                      setSelectedNewModel(value);
                      handleFormDataForNewChange(
                        'selectedModelName',
                        value?.label || null
                      );
                    }}
                  />
                </div>
              </div>

              {/* 기존 Step 2 UI 그대로 유지 */}
              <div className="flex flex-col gap-10">
                <div className="grid grid-cols-3 gap-10 align-center">
                  <div className="flex flex-col gap-2">
                    <p className="text-b2-400 text-gray-800">학습 횟수</p>
                    <input
                      type="text"
                      className="input-primary"
                      placeholder='학습 횟수를 입력하세요 (숫자)'
                      onInput={(e) => positiveNumberInputFormatter(e)}
                      value={formDataForNew.interationCount}
                      onChange={(e) =>
                        handleFormDataForNewChange(
                          'interationCount',
                          e.target.value
                        )
                      }
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <p className="text-b2-400 text-gray-800">
                      학습 데이터 시작 일시
                    </p>
                    <DateTimeInput
                      type="date"
                      value={formDataForNew.startDate}
                      onChange={(value) =>
                        handleFormDataForNewChange('startDate', value)
                      }
                      placeholder="시작일을 선택하세요"
                      max={formDataForNew.endDate}
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <p className="text-b2-400 text-gray-800">
                      학습 데이터 종료 일시
                    </p>
                    <DateTimeInput
                      type="date"
                      value={formDataForNew.endDate}
                      onChange={(value) =>
                        handleFormDataForNewChange('endDate', value)
                      }
                      placeholder="종료일을 선택하세요"
                      min={formDataForNew.startDate}
                    />
                  </div>
                </div>

                {predictionModelType === 'cbl' && (
                  <div className="flex flex-col gap-2 mt-14">
                    <p className="text-b2-400 text-gray-800">
                      충전량 예측 모듈 선택
                    </p>
                    <AiModuleSelect
                      mode="demand"
                      selectedModule={selectedChargingModule}
                      setSelectedModule={setSelectedChargingModule}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="modal-footer">
          <button
            className={`btn btn-primary big-btn ${!canComplete ? 'disabled' : ''}`}
            onClick={handleComplete}
            disabled={!canComplete || createAiLearning.isPending}
          >
            {createAiLearning.isPending ? '등록 중...' : '완료'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AiLearningRequestModal;